import React, { useEffect, useRef } from 'react'
import { atom } from 'jotai'
import isString from 'lodash/isString'
import create from 'zustand'

type GridApi = {
  api: any
  columnApi: any
}

type State = {
  apis: Record<string, GridApi>
  setApi: (instanceName: string, gridApi: GridApi) => void
}

const useGridApis = create<State>((set) => ({
  apis: {},
  setApi: (instanceName, gridApi) => {
    set((state) => ({
      apis: {
        ...state.apis,
        [instanceName]: gridApi,
      },
    }))
  },
}))

const gridApisAtom = atom<Record<string, GridApi>>({})

type Update = {
  instanceName: string
  gridApi: GridApi
}

export const setGridApiAtom = atom(null, (get, set, { instanceName, gridApi }: Update) => {
  set(gridApisAtom, (apis) => ({
    ...apis,
    [instanceName]: gridApi,
  }))
})

export const useSetGridApi = () => {
  return useGridApis((state) => state.setApi)
}

export const useGridApi = (instanceName: string) => {
  return useGridApis((state) => state.apis[instanceName])
}

export const useGridApiRef = (instanceName: string) => {
  const gridApi = useGridApi(instanceName)
  const gridApiRef = useRef<GridApi>(gridApi)

  useEffect(() => {
    gridApiRef.current = gridApi
  })

  return gridApiRef
}

interface WithGridApiProps {}

export const withGridApi =
  (getInstanceName: string | ((props) => string)) =>
  <P extends object>(Component: React.ComponentType<P & WithGridApiProps>): React.FC<P> =>
  (props) => {
    const instanceName = isString(getInstanceName) ? getInstanceName : getInstanceName(props)

    const gridApi = useGridApi(instanceName)

    return <Component {...props} gridApi={gridApi} />
  }
